import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Github, Sparkles } from "lucide-react";
import { projects, profile, accentTokens } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AnimatedBackground } from "../components/AnimatedBackground";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) return <Navigate to="/" replace />;

  const idx = projects.findIndex((p) => p.id === id);
  const next = projects[(idx + 1) % projects.length];
  const cs = project.caseStudy;
  const t = accentTokens[project.accent] || accentTokens.cyan;

  return (
    <div className="relative min-h-screen text-white">
      <AnimatedBackground />
      <Navbar />

      <main className="relative">
        {/* HERO */}
        <section
          className="relative pt-32 pb-16 md:pt-40 md:pb-20"
          data-testid={`case-study-hero-${project.id}`}
        >
          <div className="relative max-w-5xl mx-auto px-6 md:px-10">
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
              <Link
                to="/#projects"
                className="inline-flex items-center gap-2 text-sm text-cyan-100/70 hover:text-white transition-colors duration-200"
                data-testid="case-study-back"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to all projects
              </Link>
            </motion.div>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className={`mt-10 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass`}
            >
              <Sparkles className={`h-3.5 w-3.5 ${t.text}`} />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-100/90">
                Case Study {project.index} — {project.category}
              </span>
            </motion.p>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.02] text-balance"
              data-testid="case-study-title"
            >
              {project.title}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-6 font-serif italic text-xl sm:text-2xl text-gradient-aurora animate-gradient-pan max-w-3xl leading-snug"
            >
              {cs.tagline}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 grid sm:grid-cols-3 gap-3"
            >
              {[
                ["Role", cs.role],
                ["Year", cs.year],
              ].map(([k, v]) => (
                <div key={k} className="gradient-border rounded-xl glass p-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/70">
                    {k}
                  </p>
                  <p className="mt-2 text-white font-medium text-sm">{v}</p>
                </div>
              ))}
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="gradient-border rounded-xl glass p-5 hover:bg-white/[0.06] transition-colors duration-200 group"
                data-testid="case-study-github"
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/70">
                  Source
                </p>
                <p className={`mt-2 inline-flex items-center gap-1.5 text-white font-medium text-sm group-hover:text-cyan-300 transition-colors`}>
                  <Github className="h-3.5 w-3.5" />
                  GitHub
                  <ArrowUpRight className="h-3 w-3" />
                </p>
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={5}
              className={`mt-12 relative aspect-[16/9] rounded-2xl overflow-hidden gradient-border glass ${t.glow}`}
            >
              <img
                src={project.image}
                alt={project.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className={`absolute inset-0 bg-gradient-to-tr from-[#07061A]/80 via-${project.accent === "cyan" ? "cyan" : project.accent === "pink" ? "pink" : project.accent === "indigo" ? "indigo" : "violet"}-900/30 to-transparent`} />
              <span className={`absolute top-4 left-4 font-mono text-[10px] uppercase tracking-widest ${t.text}`}>
                {project.category}
              </span>
              <span className="absolute bottom-6 left-6 font-mono text-7xl sm:text-8xl font-bold text-white/15">
                {project.index}
              </span>
            </motion.div>
          </div>
        </section>

        {/* METRICS */}
        <section className="relative py-12" data-testid="case-study-metrics">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300 mb-6">
              // Outcomes
            </p>
            <div className={`grid grid-cols-2 ${project.metrics.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}>
              {project.metrics.map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="gradient-border rounded-xl glass p-5"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200/70">
                    {m.label}
                  </p>
                  <p className={`font-mono mt-2 text-3xl font-bold ${t.text} leading-tight`}>
                    {m.value}
                  </p>
                  <p className="font-mono text-[10px] text-cyan-200/50 mt-1">
                    {m.caption}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
                // 01
              </p>
              <h2 className="font-display mt-3 text-3xl font-bold tracking-tighter text-white">
                The problem
              </h2>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-9 text-cyan-50/90 text-lg leading-relaxed"
              data-testid="case-study-problem"
            >
              {cs.problem}
            </motion.p>
          </div>
        </section>

        {/* ARCHITECTURE */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
                // 02
              </p>
              <h2 className="font-display mt-3 text-3xl font-bold tracking-tighter text-white">
                Architecture
              </h2>
              <p className="mt-4 text-sm text-cyan-100/70 leading-relaxed">
                The data flow from request to response — each node owns one
                responsibility.
              </p>
            </div>

            <div className="lg:col-span-9">
              <div className="relative gradient-border rounded-2xl glass p-6 sm:p-8">
                <div className={`absolute -top-16 -right-16 h-44 w-44 ${t.blob} blur-3xl rounded-full pointer-events-none`} />
                <ol
                  className="relative space-y-3"
                  data-testid="case-study-architecture"
                >
                  {cs.architecture.map((step, i) => (
                    <motion.li
                      key={step.node}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{
                        duration: 0.5,
                        delay: i * 0.06,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="grid grid-cols-[40px_1fr] sm:grid-cols-[60px_240px_1fr] items-center gap-4"
                    >
                      <span className="font-mono text-xs text-cyan-200/70">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-md glass border ${t.chip}`}>
                        <span className={`h-1.5 w-1.5 rounded-full bg-gradient-to-br ${t.ring}`} />
                        <span className="font-mono text-xs sm:text-sm text-white">
                          {step.node}
                        </span>
                      </div>
                      <span className="text-sm text-cyan-100/75">{step.note}</span>
                    </motion.li>
                  ))}
                </ol>

                <p className="relative mt-6 pt-5 border-t border-white/5 font-mono text-[10px] uppercase tracking-widest text-cyan-200/60">
                  flow ⟶ each step measured, observable, replaceable
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* DECISIONS */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
                // 03
              </p>
              <h2 className="font-display mt-3 text-3xl font-bold tracking-tighter text-white">
                Key decisions
              </h2>
              <p className="mt-4 text-sm text-cyan-100/70 leading-relaxed">
                Engineering choices — and the tradeoffs behind them.
              </p>
            </div>

            <div className="lg:col-span-9 space-y-4">
              {cs.decisions.map((d, i) => (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="gradient-border rounded-xl glass p-5 sm:p-6 hover:bg-white/[0.05] transition-colors duration-300"
                  data-testid={`case-study-decision-${i}`}
                >
                  <div className="flex items-baseline gap-3">
                    <span className={`font-mono text-xs ${t.text}`}>D{i + 1}</span>
                    <h3 className="font-display text-lg sm:text-xl font-semibold text-white tracking-tight">
                      {d.title}
                    </h3>
                  </div>
                  <p className="mt-3 pl-8 text-cyan-100/80 text-sm sm:text-base leading-relaxed">
                    {d.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
                // 04
              </p>
              <h2 className="font-display mt-3 text-3xl font-bold tracking-tighter text-white">
                Results
              </h2>
            </div>

            <ul className="lg:col-span-9 space-y-3" data-testid="case-study-results">
              {cs.results.map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 text-cyan-50/90 text-base sm:text-lg leading-relaxed"
                >
                  <span className={`mt-2 h-1.5 w-1.5 rounded-full bg-gradient-to-br ${t.ring} flex-shrink-0`} />
                  {r}
                </motion.li>
              ))}
            </ul>
          </div>
        </section>

        {/* LESSONS + WHAT NEXT */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="relative gradient-border rounded-2xl glass p-6 sm:p-8"
            >
              <div className={`absolute -top-12 -right-12 h-32 w-32 ${t.blob} blur-3xl rounded-full pointer-events-none`} />
              <p className={`relative font-mono text-[11px] uppercase tracking-[0.3em] ${t.text}`}>
                // What I learned
              </p>
              <p className="relative font-serif mt-4 text-xl sm:text-2xl italic text-cyan-50/95 leading-snug">
                {cs.lessons}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative gradient-border rounded-2xl glass p-6 sm:p-8"
            >
              <div className="absolute -top-12 -left-12 h-32 w-32 bg-cyan-400/20 blur-3xl rounded-full pointer-events-none" />
              <p className="relative font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
                // What&rsquo;s next
              </p>
              <p className="relative mt-4 text-base sm:text-lg text-cyan-100/85 leading-relaxed">
                {cs.whatNext}
              </p>
            </motion.div>
          </div>
        </section>

        {/* STACK + NEXT */}
        <section className="relative py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-200/70 mr-2">
                Stack:
              </p>
              {project.stack.map((s) => (
                <span
                  key={s}
                  className={`font-mono text-xs px-2.5 py-1 rounded border ${t.chip}`}
                >
                  {s}
                </span>
              ))}
            </div>

            <Link
              to={`/projects/${next.id}`}
              className="mt-14 group relative block gradient-border rounded-2xl glass hover:bg-white/[0.06] transition-colors duration-300 p-6 sm:p-10"
              data-testid="case-study-next-link"
            >
              <div className="absolute -top-16 -right-16 h-40 w-40 bg-fuchsia-500/20 blur-3xl rounded-full pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <p className={`font-mono text-[11px] uppercase tracking-[0.3em] ${accentTokens[next.accent]?.text || "text-cyan-300"}`}>
                    Next case study {next.index}
                  </p>
                  <h3 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-tight">
                    {next.title}
                  </h3>
                </div>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-white group-hover:text-cyan-300 transition-colors">
                  Read
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </span>
              </div>
            </Link>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-white/5">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-cyan-100/70 hover:text-white transition-colors duration-200"
                data-testid="case-study-back-home"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to portfolio
              </Link>
              <a
                href={`mailto:${profile.email}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-cyan-400 via-teal-400 to-sky-500 hover:from-cyan-300 hover:via-teal-300 hover:to-sky-400 transition-[background] duration-300 glow-violet"
              >
                Get in touch
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
